import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getMyApiBaseUrl } from '../utils/getApiBaseUrl';
import { stHskDel } from '@/constant/value';

// 定义token的类型
export interface Token {
  address: string;
  decimals: string | null;
  name: string | null;
  symbol: string | null;
  total_supply: string | null;
  type: string;
  icon_url: string | null;
  source_platform: string;
}

// 新API返回的数据结构
interface ApiTokenData {
  address: string;
  chainId: number;
  createdAt: string;
  dataSource: string;
  decimals: number;
  dexMarketCap: string;
  firstOnlineTime: string;
  holderAddressCount: string;
  id: number;
  isRiskStablecoin: boolean;
  isRiskToken: boolean;
  jumpDexUrl: string;
  logoUrl: string;
  marketCap: string;
  name: string;
  price: string;
  priceChangeRate: string;
  priceUpdatedAt: string;
  rank: number;
  symbol: string;
  tokenType: string;
  txAmount24h: string;
  updatedAt: string;
}

// 数据转换函数
const transformApiDataToToken = (apiData: ApiTokenData): Token => {
  // 处理icon_url的替换逻辑
  // let iconUrl = apiData.logoUrl;
  // if (iconUrl === 'https://hyperindex.4everland.store/tether-usdt.png') {
  //   iconUrl = '/img/tether-usdt-logo.png';
  // }
  
  // // 为USDC.e和WETH设置默认本地图标
  // if ((apiData.symbol === 'USDC.e' || apiData.symbol === 'WETH') && !iconUrl) {
  //   iconUrl = `/img/${apiData.symbol.toLowerCase()}.svg`;
  // }

  return {
    address: apiData.address,
    decimals: apiData.decimals.toString(),
    name: apiData.name,
    symbol: apiData.symbol,
    total_supply: null, // 新API中没有这个字段
    type: apiData.tokenType === 'ERC20' ? 'ERC-20' : apiData.tokenType,
    icon_url: apiData.logoUrl,
    source_platform: apiData.dataSource || 'unknown'
  };
};

// 添加OKB链的默认token数据
const getOKBChainTokens = (): Token[] => {
  const tokens = [
    {
      address: '0x0000000000000000000000000000000000000000', // 原生OKB地址
      decimals: '18',
      name: 'OKB',
      symbol: 'OKB',
      total_supply: null,
      type: 'NATIVE',
      icon_url: '/images/okb.png',
      source_platform: 'okb_chain'
    },
  ];

  tokens.push(
    {
      address: '0x43eb40243cc0f0cd7141ff1cbf0e4447fe539f3e', // TEST-USDT
      decimals: '6',
      name: 'Test USDT',
      symbol: 'USDT',
      total_supply: null,
      type: 'ERC-20',
      icon_url: '/images/tether-usdt-logo.png',
      source_platform: 'test'
    },
    {
      address: '0x46fe1719e4218a8a0ccfe62fa7af305df128153a', // TEST-USDC
      decimals: '6',
      name: 'Test USDC',
      symbol: 'USDC',
      total_supply: null,
      type: 'ERC-20',
      icon_url: '/images/usdc.e.svg',
      source_platform: 'test'
    }
  );

  return tokens;
};

// State类型
interface TokenListState {
  items: Token[];
  loading: boolean;
  error: string | null;
  lastUpdated: number | null;
}

// 初始state
const initialState: TokenListState = {
  items: [],
  loading: false,
  error: null,
  lastUpdated: null
};


const TOKEN_PATH =  process.env.BUILD_ENV === 'test'  ?  '/api/xgenswap-v3/tokens' : '/api/xgenswap-v3/tokens'


// 异步获取token列表
export const fetchTokenList = createAsyncThunk(
  'tokenList/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const newBaseUrl = getMyApiBaseUrl();
      
      let allTokens: Token[] = [];
      let page = 1;
      const pageSize = 30; // 改为30，匹配API的limit
      let hasMoreData = true;
      
      while (hasMoreData) {
        const apiUrl = `${newBaseUrl}${TOKEN_PATH}?page=${page}&pageSize=${pageSize}`;
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        // 转换API数据为Token格式
        const transformedTokens = data.tokens?.map((apiToken: ApiTokenData) => 
          transformApiDataToToken(apiToken)
        ) || [];

        allTokens = [...allTokens, ...transformedTokens];

        // 检查是否还有更多数据
        if (page >= 2 || data.page >= data.totalPages) {
          hasMoreData = false;
        } else {
          page++;
        }
      }

      // 添加OKB链的默认token数据
      const okbChainTokens = getOKBChainTokens();

      allTokens = [...okbChainTokens, ...allTokens];

      return allTokens;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : '获取token列表失败');
    }
  }
);

// 添加手动刷新action
export const refreshTokenList = createAsyncThunk(
  'tokenList/refresh',
  async (_, { rejectWithValue }) => {
    try {
    const newBaseUrl = getMyApiBaseUrl();
    let allTokens: Token[] = [];
    let page = 1;
    const pageSize = 30; // 改为30，匹配API的limit
    let hasMoreData = true;
    
    while (hasMoreData) {
      const response = await fetch(`${newBaseUrl}${TOKEN_PATH}?page=${page}&pageSize=${pageSize}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      // 转换API数据为Token格式
      const transformedTokens = data.tokens?.map((apiToken: ApiTokenData) => 
        transformApiDataToToken(apiToken)
      ) || [];

      // 过滤掉指定地址的token
      const filteredItems = transformedTokens.filter((token: Token) => 
        token.address.toLowerCase() !== stHskDel.toLowerCase()
      );

      allTokens = [...allTokens, ...filteredItems];
      
      // 检查是否还有更多数据
      if (page >= 2 || data.page >= data.totalPages) {
        hasMoreData = false;
      } else {
        page++;
      }
    }
    
          // 添加OKB链的默认token数据
      const okbChainTokens = getOKBChainTokens();
      allTokens = [...okbChainTokens, ...allTokens];
      
      return allTokens;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : '刷新token列表失败');
    }
  }
);

// Slice
const tokenListSlice = createSlice({
  name: 'tokenList',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTokenList.pending, (state) => {
        if (!state.lastUpdated) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchTokenList.fulfilled, (state, action) => {
        const filteredTokens = action.payload.filter((token: Token) => 
          token.name !== null
        );
        
        // 对tokens进行排序，将USDT、USDC、WETH排在前面
        state.items = filteredTokens.sort((a: Token, b: Token) => {
          const prioritySymbols = ['OKB', 'WOKB', 'USDT', 'USDC.e', 'WETH'];
          const aIndex = prioritySymbols.indexOf(a.symbol || '');
          const bIndex = prioritySymbols.indexOf(b.symbol || '');
          
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return 0;
        });
        
        state.loading = false;
        state.lastUpdated = Date.now();
      })
      .addCase(fetchTokenList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? '未知错误';
      })
      .addCase(refreshTokenList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshTokenList.fulfilled, (state, action) => {
        const filteredTokens = action.payload.filter((token: Token) => 
          token.name !== null
        );
        
        // 对tokens进行排序，将USDT、USDC、WETH排在前面
        state.items = filteredTokens.sort((a: Token, b: Token) => {
          const prioritySymbols = ['OKB', 'WOKB', 'USDT', 'USDC.e', 'WETH'];
          const aIndex = prioritySymbols.indexOf(a.symbol || '');
          const bIndex = prioritySymbols.indexOf(b.symbol || '');
          
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return 0;
        });
        
        state.loading = false;
        state.lastUpdated = Date.now();
      });
  },
});


// 选择器 - 使用泛型避免循环依赖
export const selectTokens = (state: any) => {
  return state.tokenList?.items || [];
};

export const selectTokensLoading = (state: any) => {
  return state.tokenList?.loading || false;
};

export const selectTokensError = (state: any) => {
  return state.tokenList?.error || null;
};

export const selectTokensLastUpdated = (state: any) => {
  return state.tokenList?.lastUpdated || null;
};

// 派生选择器
export const selectTokensCount = (state: any) => {
  return selectTokens(state).length;
};

export const selectTokenBySymbol = (state: any, symbol: string) => {
  return selectTokens(state).find(
    (token: Token) => token.symbol?.toLowerCase() === symbol.toLowerCase()
  );
};

export const selectTokenByAddress = (state: any, address: string) => {
  return selectTokens(state).find(
    (token: Token) => token.address.toLowerCase() === address.toLowerCase()
  );
};

export const selectTokensByType = (state: any, type: string) => {
  return selectTokens(state).filter(
    (token: Token) => token.type === type
  );
};

export const selectPriorityTokens = (state: any) => {
  const prioritySymbols = ['OKB', 'WOKB', 'USDT', 'USDC.e', 'WETH'];
  return selectTokens(state).filter(
    (token: Token) => prioritySymbols.includes(token.symbol || '')
  );
};

export const selectNonPriorityTokens = (state: any) => {
  const prioritySymbols = ['OKB', 'WOKB', 'USDT', 'USDC.e', 'WETH'];
  return selectTokens(state).filter(
    (token: Token) => !prioritySymbols.includes(token.symbol || '')
  );
};

export default tokenListSlice.reducer; 